import { Link, useStaticQuery, graphql } from "gatsby";
import React from "react";
import PropTypes from "prop-types";
import styled, { useTheme } from "styled-components";
import { HiddenOnPhone, HiddenOnDesktop } from "./hidden.js";
import Image from "./image.js";
import logoSm from "../images/logo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faExternalLinkAlt,
  faLocationDot,
  faMobileScreenButton,
} from "@fortawesome/free-solid-svg-icons";
import { _ } from "../utils/i18n";
import Button from "./button.js";

const FooterWrapper = styled.footer`
  width: 100%;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.footerTextTop || theme.colors.black};
  background-color: ${({ theme }) =>
    theme.colors.footerBackgroundTop || theme.colors.secondary};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
  @media (min-width: 960px) {
    height: auto;
    margin-top: 0;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    align-items: center;
    position: relative;
    flex-direction: row;
    justify-content: center;
    height: auto;
  }
`;
const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 40px;
  justify-content: space-evenly;
  height: auto;
  @media (min-width: 960px) {
    height: auto;
    justify-content: center;
    align-items: center;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    width: 100%;
    padding: 0;
    justify-content: center;
    height: auto;
  }
`;
const MaxWidth = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.wide};
  display: flex;
  flex-flow: row wrap;
  padding: 1rem;
  justify-content: center;
  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    flex-direction: row;
    align-items: flex-start;
    padding: 20px;
    position: relative;
    margin: auto;
  }
`;
const ListMenuLinks = styled.div`
  box-sizing: border-box;
  display: flex;
  text-decoration: none;
  min-height: 285px;
  padding: 0 1rem;
  justify-content: center;
  flex-direction: column;
  margin-bottom: 30px;
  @media (min-width: 768px) {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;
    max-width: 1180px;
    align-items: flex-start;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    margin: 20px;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wide}) {
    flex-flow: column;
    padding: 0 20px;
  }
`;
const MainLinks = styled((props) => <Link {...props} />)`
  margin: 10px 0;
  font-weight: bold;
  letter-spacing: 1.5px;
  text-decoration: none;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.black};
  text-transform: capitalize;
  &:hover {
    text-decoration: underline;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    font-size: 0.9rem;
    text-transform: none;
    font-weight: 400;
  }
`;
const SubLinks = styled((props) => <Link {...props} />)`
  margin: 10px 0;
  letter-spacing: 1.5px;
  text-decoration: none;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.footerTextTop || theme.colors.black};
  text-transform: capitalize;
  font-family: var(--font-paragraph);
  &:hover {
    text-decoration: underline;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    font-size: 0.9rem;
    text-transform: none;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wide}) {
    font-size: 1rem;
  }
`;
const ExternalLinks = styled.a`
  margin: 10px 0;
  letter-spacing: 1.5px;
  text-decoration: none;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.footerTextTop || theme.colors.black};
  &:hover {
    text-decoration: underline;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    font-size: 0.9rem;
    text-transform: none;
  }
`;
const ListMenuLinksInnerWrapper = styled.div`
  display: flex;
  text-decoration: none;
  align-items: flex-start;
  margin: 10px 0;
  flex-direction: column;
  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    justify-content: center;
    margin-top: 6rem;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wide}) {
    margin-block: 1rem;
  }
`;
const OtherLinksContainer = styled.div`
  display: grid;
  background-color: ${({ theme }) =>
    theme.colors.footerBackgroundBottom || theme.colors.primary};
  width: 100%;
  height: auto;
  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    display: block;
    flex-direction: row;
    text-align: center;
    justify-content: center;
    padding: 2rem 1rem;
    min-height: 8rem;
    position: relative;
  }
`;
const LinkLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  max-width: 300px;
  margin-top: 40px;
  margin-bottom: 20px;
  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    margin-top: 0;
    align-items: flex-start;
    justify-content: flex-start;
    margin-bottom: 0;
    width: auto;
    position: absolute;
    left: 20px;
  }
`;
const OtherLink = styled((props) => <Link {...props} />)`
  letter-spacing: 1.5px;
  color: ${({ theme }) => theme.colors.footerTextBottom || theme.colors.white};
  font-size: 0.8rem;
  text-decoration: underline;
  margin: 5px 0;
  @media (min-width: 960px) {
    text-align: end;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    margin: 0 0 10px 0;
  }
  &:visited {
    color: ${({ theme }) =>
      theme.colors.footerTextBottom || theme.colors.white};
  }
`;
const OtherLinkExternal = styled.a`
  letter-spacing: 1.5px;
  color: ${({ theme }) => theme.colors.footerTextBottom || theme.colors.white};
  font-size: 0.8rem;
  text-decoration: underline;
  margin: 5px 0;
  @media (min-width: 960px) {
    text-align: end;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    margin: 0 0 10px 0;
  }
  &:visited {
    color: ${({ theme }) =>
      theme.colors.footerTextBottom || theme.colors.white};
  }
`;
const OtherLinksLabel = styled.p`
  letter-spacing: 1.5px;
  color: ${({ theme }) => theme.colors.footerTextBottom || theme.colors.white};
  margin-bottom: 0;
  font-size: 0.9rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    margin: 0 0 10px 0;
    text-align: left;
  }
`;
const RightSidecontainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 20px 40px 20px;
  width: 100%;
  justify-content: center;
  align-items: flex-start;
  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    padding: 0;
    width: auto;
    position: absolute;
    right: 20px;
  }
`;
const Logo = styled(Image)`
  width: ${({ $logoWidth }) =>
    $logoWidth ? `${$logoWidth}px!important` : "100px"};
  height: ${({ $logoHeight }) =>
    $logoHeight ? `${$logoHeight}px!important` : "50px"};
  margin-bottom: 10px;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: ${({ $logoWidthMobile }) =>
      $logoWidthMobile ? `${$logoWidthMobile}px!important` : "100px"};
    height: ${({ $logoHeightMobile }) =>
      $logoHeightMobile ? `${$logoHeightMobile}px!important` : "68px"};
  }
`;

const Adressfield = styled.p`
  font-size: 0.9rem;
  margin-block: 0.5rem;
  display: flex;
  align-items: center;
  .info-link {
    color: ${({ theme }) => theme.colors.footerTextTop || theme.colors.black};
  }
  a {
    text-decoration: none;
    font-family: var(--font-paragraph);
    @media (hover: hover) {
      &:hover {
        text-decoration: underline;
      }
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wide}) {
    font-size: 1rem;
  }
`;
const SocialMedia = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 0.5rem;
`;
const TopLabel = styled.p`
  display: none;
  margin-top: 0;
  margin-bottom: 10px;
  text-transform: capitalize;
  font-weight: 600;
  font-size: 1.1rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    padding-top: 15px;
    display: block;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    font-size: 1rem;
  }
`;
const MobileContainer = styled.div`
  max-width: 300px;
  width: 100%;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;
const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding: 0 1rem;
  }
`;
const FooterText = styled.div`
  max-width: 300px;
  font-size: 0.9rem;
  font-family: var(--font-paragraph);
  @media (max-width: ${({ theme }) => theme.breakpoints.wide}) {
    font-size: 1rem;
  }
`;
const ContactIcon = styled(FontAwesomeIcon)`
  margin-right: 0.5rem;
  font-size: 1.2rem !important;
`;
const ImagesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 6rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.wide}) {
    margin-top: 1rem;
    max-width: 50%;
  }
`;
const SocialMediaLink = styled.a`
  text-decoration: none;
  display: flex;
  align-items: center;
  min-height: 24px;
  color: ${({ theme }) => theme.colors.footerTextTop || theme.colors.black};
  p {
    margin: 0;
    padding-inline: 0.5rem;
    font-size: 0.9rem;
    @media (max-width: ${({ theme }) => theme.breakpoints.wide}) {
      font-size: 1rem;
    }
  }
  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }
`;
const StyledFooterImage = styled(Image)`
  max-width: 160px;
`;
const FooterDescription = styled.div`
  margin-block: 0.5rem;
  a {
    color: ${({ theme }) => theme.colors.footerTextTop || theme.colors.black};
  }
  p {
    margin: 0;
    font-size: 0.9rem;
  }
`;
const SvgLogo = styled.img`
  width: ${({ $logoWidth }) =>
    $logoWidth ? `${$logoWidth}px!important` : "100px"};
  height: ${({ $logoHeight }) =>
    $logoHeight ? `${$logoHeight}px!important` : "50px"};
  margin-bottom: 10px;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: ${({ $logoWidthMobile }) =>
      $logoWidthMobile ? `${$logoWidthMobile}px!important` : "100px"};
    height: ${({ $logoHeightMobile }) =>
      $logoHeightMobile ? `${$logoHeightMobile}px!important` : "50px"};
  }
`;

const StyledButton = ({ url, label }) => {
  const theme = useTheme();
  return (
    <Button
      variant="filled"
      label={label}
      backgroundColor={
        theme.colors.footerButton
          ? theme.colors.footerButton
          : theme.colors.white
      }
      textColor={
        theme.colors.footerButtonText
          ? theme.colors.footerButtonText
          : theme.colors.black
      }
      url={url}
    />
  );
};
StyledButton.propTypes = {
  url: PropTypes.string,
  label: PropTypes.string,
};

export default function Footer({ staticQueryData }) {
  const { footerNav, footerLinks, wp } =
    staticQueryData ||
    useStaticQuery(graphql`
      fragment FooterNav on WpMenuItem {
        order
        id
        url
        cssClasses
        label
        target
      }

      query FooterQuery {
        footerNav: wpMenu(locations: { eq: FOOTER_NAV }) {
          menuItems {
            nodes {
              ...FooterNav
              childItems {
                nodes {
                  ...FooterNav
                }
              }
              parentId
            }
          }
        }

        footerLinks: wpMenu(locations: { eq: FOOTER_LINKS }) {
          menuItems {
            nodes {
              ...FooterNav
              parentId
            }
          }
        }

        wp {
          generalSettings {
            title
          }
          crbThemeOptions {
            crbAddress
            crbEmail
            crbFooterText
            crbFooterDescription
            crbFooterSocialLinks {
              icon {
                json
              }
              text
              url
            }
            crbFooterImages {
              images {
                media {
                  localFile {
                    publicURL
                    childrenImageSharp {
                      gatsbyImageData
                    }
                  }
                }
              }
            }
            crbPhone
            crbLogoFooterWidth
            crbLogoMobileFooterWidth
            crbLogoFooterHeight
            crbLogoMobileFooterHeight
            crbLogoFooter {
              media {
                mimeType
                altText
                focalPoint {
                  objectPosition
                }
                localFile {
                  publicURL
                  childImageSharp {
                    gatsbyImageData(
                      height: 50
                      layout: FIXED
                      placeholder: NONE
                      transformOptions: { fit: CONTAIN }
                    )
                  }
                }
              }
            }
            crbLogoMobileFooter {
              media {
                mimeType
                altText
                localFile {
                  publicURL
                  childImageSharp {
                    gatsbyImageData(
                      placeholder: NONE
                      transformOptions: { fit: CONTAIN }
                    )
                  }
                }
              }
            }
          }
        }
      }
    `);

  React.useEffect(() => {
    if (process.env.GATSBY_CONSENT_SCRIPT_URL) {
      const script = document.createElement("script");
      script.src = process.env.GATSBY_CONSENT_SCRIPT_URL;
      script.async = true;
      script.crossOrigin = "anonymous";
      document.body.appendChild(script);

      return () => document.body.removeChild(script);
    }
  }, []);

  return (
    <FooterWrapper
      itemScope
      itemType="http://schema.org/Organization"
      aria-label="footer"
    >
      <ListContainer>
        <ListMenuLinks>
          <ListMenuLinksInnerWrapper>
            <meta itemProp="name" content={wp.generalSettings?.title} />
            <HiddenOnPhone>
              {wp.crbThemeOptions.crbLogoFooter?.media?.mimeType ===
              "image/svg+xml" ? (
                <SvgLogo
                  itemProp="image"
                  src={
                    wp.crbThemeOptions.crbLogoFooter?.media?.localFile
                      ?.publicURL
                  }
                  className="svg-size-footer"
                  alt="Logotype"
                  $logoWidth={wp?.crbThemeOptions?.crbLogoFooterWidth}
                  $logoHeight={wp?.crbThemeOptions?.crbLogoFooterHeight}
                />
              ) : wp?.crbThemeOptions?.crbLogoFooter?.media ? (
                <Logo
                  itemProp="image"
                  alt="Logotype"
                  image={wp?.crbThemeOptions?.crbLogoFooter?.media}
                  $logoWidth={wp?.crbThemeOptions?.crbLogoFooterWidth}
                  $logoHeight={wp?.crbThemeOptions?.crbLogoFooterHeight}
                />
              ) : (
                <Logo
                  itemProp="image"
                  alt="Logotype"
                  src={logoSm}
                  $logoWidth={wp?.crbThemeOptions?.crbLogoFooterWidth}
                  $logoHeight={wp?.crbThemeOptions?.crbLogoFooterHeight}
                />
              )}
            </HiddenOnPhone>
            <HiddenOnDesktop>
              {wp.crbThemeOptions.crbLogoMobileFooter?.media?.mimeType ===
              "image/svg+xml" ? (
                <SvgLogo
                  itemProp="image"
                  src={
                    wp.crbThemeOptions.crbLogoMobileFooter?.media?.localFile
                      ?.publicURL
                  }
                  className="svg-size-footer"
                  alt="Logotype"
                  $logoWidthMobile={
                    wp?.crbThemeOptions?.crbLogoMobileFooterWidth
                  }
                  $logoHeightMobile={
                    wp?.crbThemeOptions?.crbLogoMobileFooterHeight
                  }
                />
              ) : wp?.crbThemeOptions?.crbLogoMobileFooter?.media ? (
                <Logo
                  itemProp="image"
                  alt="Logotype"
                  image={wp?.crbThemeOptions?.crbLogoMobileFooter?.media}
                  $logoWidthMobile={
                    wp?.crbThemeOptions?.crbLogoMobileFooterWidth
                  }
                  $logoHeightMobile={
                    wp?.crbThemeOptions?.crbLogoMobileFooterHeight
                  }
                />
              ) : (
                <Logo
                  itemProp="image"
                  alt="Logotype"
                  src={logoSm}
                  $logoWidthMobile={
                    wp?.crbThemeOptions?.crbLogoMobileFooterWidth
                  }
                  $logoHeightMobile={
                    wp?.crbThemeOptions?.crbLogoMobileFooterHeight
                  }
                />
              )}
            </HiddenOnDesktop>
            {wp.crbThemeOptions.crbFooterText && (
              <FooterText
                dangerouslySetInnerHTML={{
                  __html: wp.crbThemeOptions.crbFooterText,
                }}
              />
            )}
            {wp?.crbThemeOptions?.crbAddress?.length > 0 && (
              <Adressfield itemProp="address">
                <ContactIcon icon={faLocationDot} />
                {wp.crbThemeOptions.crbAddress}
              </Adressfield>
            )}
            {wp?.crbThemeOptions?.crbPhone?.length > 0 && (
              <Adressfield itemProp="telephone">
                <ContactIcon icon={faMobileScreenButton} />
                <a
                  className="info-link"
                  href={`tel:${wp.crbThemeOptions.crbPhone.replaceAll(
                    " ",
                    ""
                  )}`}
                >
                  {wp.crbThemeOptions.crbPhone}
                </a>
              </Adressfield>
            )}
            {wp?.crbThemeOptions?.crbEmail?.length > 0 && (
              <Adressfield itemProp="email">
                <ContactIcon icon={faEnvelope} />
                <a
                  className="info-link"
                  href={`mailto:${wp.crbThemeOptions.crbEmail}`}
                >
                  {wp.crbThemeOptions.crbEmail}
                </a>
              </Adressfield>
            )}
            {wp.crbThemeOptions.crbFooterDescription && (
              <FooterDescription
                dangerouslySetInnerHTML={{
                  __html: wp.crbThemeOptions.crbFooterDescription,
                }}
              />
            )}

            <SocialMedia>
              {wp?.crbThemeOptions?.crbFooterSocialLinks?.length > 0 &&
                wp.crbThemeOptions.crbFooterSocialLinks.map(
                  (socialLink, index) => (
                    <SocialMediaLink
                      key={index}
                      href={socialLink.url}
                      target="_blank"
                      rel="noopener"
                    >
                      <FontAwesomeIcon
                        icon={
                          socialLink.icon?.json
                            ? JSON.parse(socialLink.icon?.json)
                            : null
                        }
                      />
                      <p>{socialLink.text}</p>
                    </SocialMediaLink>
                  )
                )}
            </SocialMedia>
          </ListMenuLinksInnerWrapper>
          {footerNav?.menuItems?.nodes
            ?.filter((node) => node.parentId === null)
            ?.map((node) => (
              <ListMenuLinksInnerWrapper key={node.id}>
                <TopLabel>{node.label}</TopLabel>
                {node.childItems?.nodes?.map((childNode) => {
                  return childNode.cssClasses?.includes("main") ? (
                    <MainLinks key={childNode.id} to={childNode.url}>
                      {childNode.label}
                    </MainLinks>
                  ) : childNode.target != null ? (
                    <ExternalLinks
                      key={childNode.id}
                      href={childNode.url}
                      target={childNode.target}
                    >
                      {childNode.label}{" "}
                      <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                    </ExternalLinks>
                  ) : (
                    <SubLinks key={childNode.id} to={childNode.url}>
                      {childNode.label}
                    </SubLinks>
                  );
                })}
              </ListMenuLinksInnerWrapper>
            ))}
          {wp.crbThemeOptions.crbFooterImages.length > 0 && (
            <ImagesWrapper>
              {wp.crbThemeOptions.crbFooterImages.map((footerImage, index) => (
                <StyledFooterImage
                  image={footerImage.images.media}
                  src={footerImage?.images?.media?.localFile?.publicURL}
                  key={index}
                  alt="footer-image"
                />
              ))}
            </ImagesWrapper>
          )}
        </ListMenuLinks>
        <OtherLinksContainer>
          <MaxWidth>
            <HiddenOnPhone>
              <LinkLabelContainer>
                <OtherLinksLabel>{_("Design og kode")}:</OtherLinksLabel>
                <OtherLinkExternal
                  href="https://enklerevalg.no"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {_("Mediebyrået Enklere Valg")}
                </OtherLinkExternal>
              </LinkLabelContainer>
            </HiddenOnPhone>
            {footerLinks?.menuItems?.nodes
              ?.filter((node) => node.parentId === null)
              ?.filter((node) => node.cssClasses?.includes("button"))
              ?.map((node) => (
                <ButtonContainer key={node.id}>
                  <StyledButton url={node.url} label={node.label} />
                </ButtonContainer>
              ))}

            <RightSidecontainer>
              <MobileContainer>
                <HiddenOnDesktop>
                  <LinkLabelContainer>
                    <OtherLinksLabel>Design og kode:</OtherLinksLabel>
                    <OtherLinkExternal
                      href="https://enklerevalg.no"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Mediebyrået Enklere Valg
                    </OtherLinkExternal>
                  </LinkLabelContainer>
                </HiddenOnDesktop>
                {footerLinks?.menuItems?.nodes
                  ?.filter(
                    (node) =>
                      !node.cssClasses?.includes("button") &&
                      !node.cssClasses?.includes("soMe")
                  )
                  ?.map((node) => (
                    <OtherLink key={node.id} to={node.url}>
                      {node.label}
                    </OtherLink>
                  ))}
              </MobileContainer>
            </RightSidecontainer>
          </MaxWidth>
        </OtherLinksContainer>
      </ListContainer>
    </FooterWrapper>
  );
}

Footer.propTypes = {
  staticQueryData: PropTypes.object,
};
